import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Collapsible } from '@cbhq/cds-web/collapsible/Collapsible';
import { useA11yControlledVisibility } from '@cbhq/cds-web/hooks/useA11yControlledVisibility';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';

import type { LEProductAccessRowSelectionState } from './LEProductAccessRow';
import { LEProductAccessRow } from './LEProductAccessRow';
import type { ProductAccessRowConfig } from './ProductAccessRow';
import { ProductAccessRow } from './ProductAccessRow';
import type { ProductIconName } from './ProductIcon';

const messages = defineMessages({
  triggerAccessibilityLabel: {
    id: 'Team.inviteTray.leProductAccessGroup.triggerAccessibilityLabel',
    defaultMessage: 'Access options for {entityName}',
    description: 'Accessibility label for the legal entity access row that controls visibility',
  },
});

export type LEProductAccessGroupRow = {
  entityId: string;
  selectionState: LEProductAccessRowSelectionState;
  onSelectionToggle: (selected: boolean) => void;
  iconName: ProductIconName;
  iconBackground: string;
  title: string;
  description?: string;
  isEditable?: boolean;
};

export type LEProductAccessGroupParams = {
  entityRow: LEProductAccessGroupRow;
  productRows: ProductAccessRowConfig[];
  defaultExpanded?: boolean;
  isEditable?: boolean;
};

export function LEProductAccessGroup({
  entityRow,
  productRows,
  defaultExpanded,
  isEditable = true,
}: LEProductAccessGroupParams) {
  const { formatMessage } = useIntl();

  // Expansion is only changed via user toggles—not by product selection alone.
  // This prevents collapsing when the last product is unchecked, keeping context visible.
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(
    defaultExpanded ?? entityRow.selectionState === 'indeterminate',
  );
  const isExpanded = uncontrolledExpanded;

  const { triggerAccessibilityProps, controlledElementAccessibilityProps } =
    useA11yControlledVisibility(isExpanded, {
      accessibilityLabel: formatMessage(messages.triggerAccessibilityLabel, {
        entityName: entityRow.title,
      }),
    });

  const handleSelectionToggle = useCallback(
    (selected: boolean) => {
      // Auto-expand once on the empty -> selected transition so the seeded
      // default product is visible. Unchecking never collapses.
      if (selected) {
        setUncontrolledExpanded(true);
      }
      entityRow.onSelectionToggle(selected);
    },
    [entityRow],
  );

  const handleExpandToggle = useCallback(() => {
    setUncontrolledExpanded(!isExpanded);
  }, [isExpanded]);

  return (
    <VStack gap={1} width="100%">
      <LEProductAccessRow
        {...entityRow}
        isExpanded={isExpanded}
        onExpandToggle={handleExpandToggle}
        triggerAccessibilityProps={triggerAccessibilityProps}
        onSelectionToggle={handleSelectionToggle}
        isEditable={entityRow.isEditable ?? isEditable}
      />
      <Collapsible collapsed={!isExpanded} {...controlledElementAccessibilityProps}>
        <HStack width="100%" alignItems="stretch" gap={4} paddingTop={1} paddingStart={1.5}>
          <Box width={1} flexShrink={0} background="bgLine" />
          <VStack gap={2} width="100%">
            {productRows.map((productRow) => (
              <ProductAccessRow
                key={productRow.productKey}
                {...productRow}
                isEditable={productRow.isEditable ?? isEditable}
              />
            ))}
          </VStack>
        </HStack>
      </Collapsible>
    </VStack>
  );
}
